import { useMemo, useState, useCallback } from 'react';
import { Button } from '../../../core/button';
import { ExerciseType } from '@org/model';
import { cn } from '../../../../utils';
import { AlertCircle, CheckCircle2, Link2, Sparkles } from 'lucide-react';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ExerciseFeedbackList } from '../../components/shared/exercise-feedback-list';
import { ContentDescription } from '../../components/shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';

interface MatchingExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

interface MatchingPair {
  pairId: string;
  promptId: string;
  promptLabel: string;
  optionId: string;
  optionLabel: string;
  order: number;
}

interface MatchState {
  promptId: string;
  optionId: string;
  colorIndex: number;
}

const MATCH_COLORS = [
  {
    bg: 'bg-sky-100',
    border: 'border-sky-400',
    text: 'text-sky-700',
    ring: 'ring-sky-300',
    badge: 'bg-sky-500',
    glow: 'shadow-sky-200',
  },
  {
    bg: 'bg-amber-100',
    border: 'border-amber-400',
    text: 'text-amber-700',
    ring: 'ring-amber-300',
    badge: 'bg-amber-500',
    glow: 'shadow-amber-200',
  },
  {
    bg: 'bg-emerald-100',
    border: 'border-emerald-400',
    text: 'text-emerald-700',
    ring: 'ring-emerald-300',
    badge: 'bg-emerald-500',
    glow: 'shadow-emerald-200',
  },
  {
    bg: 'bg-violet-100',
    border: 'border-violet-400',
    text: 'text-violet-700',
    ring: 'ring-violet-300',
    badge: 'bg-violet-500',
    glow: 'shadow-violet-200',
  },
  {
    bg: 'bg-orange-100',
    border: 'border-orange-400',
    text: 'text-orange-700',
    ring: 'ring-orange-300',
    badge: 'bg-orange-500',
    glow: 'shadow-orange-200',
  },
  {
    bg: 'bg-teal-100',
    border: 'border-teal-400',
    text: 'text-teal-700',
    ring: 'ring-teal-300',
    badge: 'bg-teal-500',
    glow: 'shadow-teal-200',
  },
  {
    bg: 'bg-pink-100',
    border: 'border-pink-400',
    text: 'text-pink-700',
    ring: 'ring-pink-300',
    badge: 'bg-pink-500',
    glow: 'shadow-pink-200',
  },
];

type MatchColor = typeof MATCH_COLORS[number];

/** Seeded Fisher-Yates shuffle for deterministic option ordering. */
function seededShuffle<T>(items: T[], seed: number): T[] {
  let state = seed;
  const random = () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/** Compute card classes based on evaluation, selection, and match state. */
function getCardStyles(opts: {
  grade: string | null;
  isSelected: boolean;
  isMatched: boolean;
  matchColor: MatchColor | null;
  isReadOnly: boolean;
}): string {
  const { grade, isSelected, isMatched, matchColor, isReadOnly } = opts;
  const base = cn(
    'relative flex items-center gap-1.5 sm:gap-3 rounded-xl sm:rounded-2xl border-2 px-2.5 sm:px-4 py-2.5 sm:py-3.5 transition-all duration-200 cursor-pointer',
    'min-h-[48px] sm:min-h-[52px]',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    isReadOnly && 'cursor-default'
  );

  if (grade && isMatched && matchColor) {
    return cn(
      base, matchColor.border, matchColor.bg, matchColor.text, 'shadow-md', matchColor.glow,
      grade === 'perfect' && 'ring-[3px] ring-emerald-400/60 ring-offset-2',
      grade === 'wrong' && 'ring-[3px] ring-rose-400/60 ring-offset-2 animate-shake',
      !isReadOnly && 'hover:ring-offset-3'
    );
  }
  if (grade === 'perfect') {
    return cn(base, 'border-emerald-400 bg-emerald-50 text-emerald-900 shadow-md shadow-emerald-100 ring-[3px] ring-emerald-400/60 ring-offset-2', !isReadOnly && 'hover:border-emerald-500 hover:bg-emerald-100');
  }
  if (grade === 'wrong') {
    return cn(base, 'border-rose-400 bg-rose-50 text-rose-900 shadow-md shadow-rose-100 ring-[3px] ring-rose-400/60 ring-offset-2 animate-shake', !isReadOnly && 'hover:border-rose-500 hover:bg-rose-100');
  }
  if (isSelected) {
    return cn(base, 'border-primary bg-primary/5 ring-2 ring-primary/30 scale-[1.02] shadow-lg shadow-primary/20 z-10');
  }
  if (isMatched && matchColor) {
    return cn(base, matchColor.border, matchColor.bg, matchColor.text, 'shadow-md', matchColor.glow);
  }
  return cn(base, 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 hover:shadow-md active:scale-[0.98]');
}

interface CardProps {
  id: string;
  label: string;
  isSelected: boolean;
  matchColor: MatchColor | null;
  matchNumber: number | null;
  evaluation?: {
    isCorrect: boolean;
    grade?: 'perfect' | 'near' | 'partial' | 'wrong';
  } | null;
  isReadOnly: boolean;
  onClick: () => void;
  type: 'prompt' | 'option';
  hasEvaluation?: boolean;
}

function MatchCard({
  id,
  label,
  isSelected,
  matchColor,
  matchNumber,
  evaluation,
  isReadOnly,
  onClick,
  type,
  hasEvaluation = false,
}: CardProps) {
  const isMatched = matchColor !== null;
  const grade = evaluation
    ? evaluation.grade ?? (evaluation.isCorrect ? 'perfect' : 'wrong')
    : null;

  const cardStyles = getCardStyles({ grade, isSelected, isMatched, matchColor, isReadOnly });

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isReadOnly}
      className={cardStyles}
      aria-label={`${type === 'prompt' ? 'Prompt' : 'Option'}: ${label}`}
      aria-pressed={isSelected || isMatched}
    >
      {/* Match number badge - show even with evaluation */}
      {matchNumber !== null && (
        <span
          className={cn(
            'absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center',
            'rounded-full text-[10px] sm:text-xs font-bold text-white shadow-md',
            'transition-transform duration-200',
            matchColor?.badge ?? 'bg-slate-500'
          )}
        >
          {matchNumber}
        </span>
      )}

      {/* Evaluation icons with colored background circles */}
      {grade === 'perfect' && (
        <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-emerald-500 shadow-sm">
          <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
        </span>
      )}
      {grade === 'wrong' && (
        <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-rose-500 shadow-sm">
          <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
        </span>
      )}

      {/* Link icon for matched items */}
      {isMatched && !grade && (
        <span className={cn('flex-shrink-0 hidden sm:block', matchColor?.text ?? 'text-slate-400')}>
          <Link2 className="h-4 w-4" />
        </span>
      )}

      {/* Content */}
      <span className="flex-1 text-xs sm:text-sm font-medium leading-snug text-left break-words">
        {renderInlineMarkdown(label, `${type}-${id}`)}
      </span>

      {/* Selection indicator */}
      {isSelected && !isMatched && !grade && (
        <span className="flex-shrink-0 hidden sm:block">
          <Sparkles className="h-4 w-4 text-primary animate-pulse" />
        </span>
      )}
    </button>
  );
}

export function MatchingExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: MatchingExerciseProps) {
  const pairs: MatchingPair[] = useMemo(() => {
    const rawPairs = content.pairs ?? [];
    return rawPairs.map((pair: any, index: number) => ({
      pairId: pair.pairId ?? `pair-${index}`,
      promptId: pair.promptId ?? pair.pairId ?? `prompt-${index}`,
      promptLabel: pair.promptLabel ?? '',
      optionId: pair.optionId ?? `option-${index}`,
      optionLabel: pair.optionLabel ?? '',
      order: pair.order ?? index,
    }));
  }, [content.pairs]);

  const lastSubmission = useMemo(() => {
    const submission = content.lastSubmission;
    if (submission?.type === ExerciseType.Matching) {
      return submission;
    }
    return null;
  }, [content.lastSubmission]);

  const lastEvaluationDetails = useMemo(() => {
    const evaluation = (content.lastEvaluation as any) ?? null;
    const autoDetails = (evaluation?.auto as any)?.details ?? evaluation?.details;
    return autoDetails?.kind === 'Matching' ? autoDetails : null;
  }, [content.lastEvaluation]);

  // Build sets of valid IDs for quick lookup
  const validPromptIds = useMemo(() => new Set(pairs.map((p) => p.promptId)), [pairs]);
  const validOptionIds = useMemo(() => new Set(pairs.map((p) => p.optionId)), [pairs]);

  // Map promptId to its index for consistent color coding
  const promptIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    pairs.forEach((pair, index) => {
      map.set(pair.promptId, index);
    });
    return map;
  }, [pairs]);

  // Get consistent color index based on prompt position (not selection order)
  const getColorIndexForPrompt = useCallback(
    (promptId: string) => {
      const index = promptIndexMap.get(promptId) ?? 0;
      return index % MATCH_COLORS.length;
    },
    [promptIndexMap]
  );

  // Initialize matches from last submission, filtering out stale IDs that no longer exist
  const [matches, setMatches] = useState<MatchState[]>(() => {
    if (lastSubmission?.matches) {
      // Filter out matches that reference IDs no longer in the current exercise definition
      return lastSubmission.matches
        .filter((m: any) => 
          validPromptIds.has(m.promptId) && validOptionIds.has(m.selectedOptionId)
        )
        .map((m: any) => {
          const promptIndex = pairs.findIndex((p) => p.promptId === m.promptId);
          return {
            promptId: m.promptId,
            optionId: m.selectedOptionId,
            colorIndex: (promptIndex >= 0 ? promptIndex : 0) % MATCH_COLORS.length,
          };
        });
    }
    return [];
  });

  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Track if user has modified matches since last evaluation (to hide evaluation feedback)
  const [hasModifiedSinceEvaluation, setHasModifiedSinceEvaluation] = useState(false);

  const shuffledOptions = useMemo(() => {
    const options = pairs.map((pair) => ({
      optionId: pair.optionId,
      optionLabel: pair.optionLabel,
      correctPromptId: pair.promptId,
    }));
    const seed = (content.id ?? 'default').split('').reduce(
      (acc, char) => acc + char.charCodeAt(0),
      0
    );
    return seededShuffle(options, seed);
  }, [pairs, content.id]);

  // Build evaluation map - only show if user hasn't modified since evaluation
  // Also filter out evaluations for IDs that no longer exist in the current exercise
  const evaluationMap = useMemo(() => {
    // Don't show evaluation if user has modified their matches
    if (hasModifiedSinceEvaluation || !lastEvaluationDetails?.pairEvaluations) {
      return new Map<string, { isCorrect: boolean; grade?: string }>();
    }

    const map = new Map<string, { isCorrect: boolean; grade?: string }>();
    for (const evaluation of lastEvaluationDetails.pairEvaluations) {
      // Only include evaluations for IDs that still exist in the current exercise
      if (evaluation.selectedOptionId && validOptionIds.has(evaluation.selectedOptionId)) {
        map.set(evaluation.selectedOptionId, {
          isCorrect: evaluation.isCorrect,
          grade: evaluation.isCorrect ? 'perfect' : 'wrong',
        });
      }
      if (evaluation.promptId && validPromptIds.has(evaluation.promptId)) {
        map.set(evaluation.promptId, {
          isCorrect: evaluation.isCorrect,
          grade: evaluation.isCorrect ? 'perfect' : 'wrong',
        });
      }
    }
    return map;
  }, [lastEvaluationDetails, hasModifiedSinceEvaluation, validPromptIds, validOptionIds]);

  // Get match info for an item
  const getMatchInfo = useCallback(
    (id: string, type: 'prompt' | 'option') => {
      const match = matches.find((m) =>
        type === 'prompt' ? m.promptId === id : m.optionId === id
      );
      if (!match) return { isMatched: false, colorIndex: null, matchNumber: null };

      const matchIndex = matches.indexOf(match);
      return {
        isMatched: true,
        colorIndex: match.colorIndex,
        matchNumber: matchIndex + 1,
      };
    },
    [matches]
  );

  const handleCardClick = useCallback(
    (id: string, type: 'prompt' | 'option') => {
      if (isReadOnly) return;

      if (lastEvaluationDetails && !hasModifiedSinceEvaluation) {
        setHasModifiedSinceEvaluation(true);
      }

      const isPrompt = type === 'prompt';
      const matchKey = isPrompt ? 'promptId' : 'optionId';
      const counterpartSelected = isPrompt ? selectedOptionId : selectedPromptId;

      // If already matched, unmatch it
      const existingMatch = matches.find((m) => m[matchKey] === id);
      if (existingMatch) {
        setMatches((prev) => prev.filter((m) => m[matchKey] !== id));
        return;
      }

      // If counterpart is selected, create a match
      if (counterpartSelected) {
        const counterpartKey = isPrompt ? 'optionId' : 'promptId';
        const alreadyMatched = matches.find((m) => m[counterpartKey] === counterpartSelected);
        if (!alreadyMatched) {
          const promptId = isPrompt ? id : counterpartSelected;
          const optionId = isPrompt ? counterpartSelected : id;
          setMatches((prev) => [
            ...prev,
            { promptId, optionId, colorIndex: getColorIndexForPrompt(promptId) },
          ]);
        }
        setSelectedPromptId(null);
        setSelectedOptionId(null);
        return;
      }

      // Toggle selection
      if (isPrompt) {
        setSelectedPromptId((prev) => (prev === id ? null : id));
        setSelectedOptionId(null);
      } else {
        setSelectedOptionId((prev) => (prev === id ? null : id));
        setSelectedPromptId(null);
      }
    },
    [isReadOnly, matches, selectedPromptId, selectedOptionId, getColorIndexForPrompt, lastEvaluationDetails, hasModifiedSinceEvaluation]
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Build matches based on current state
      const matchesSubmission = pairs.map((pair) => {
        const match = matches.find((m) => m.promptId === pair.promptId);
        return {
          promptId: pair.promptId,
          selectedOptionId: match?.optionId ?? '',
        };
      });

      await onExerciseSubmit(content, {
        type: ExerciseType.Matching,
        matches: matchesSubmission,
      });
      
      // Reset modification flag so new evaluation results will show
      setHasModifiedSinceEvaluation(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = matches.length > 0;
  const allMatched = matches.length === pairs.length;

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'space-y-4 sm:space-y-6',
        variant === 'card' && 'rounded-xl sm:rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm'
      )}
    >
      {content.title && (
        <h3 className="text-lg sm:text-xl font-semibold text-foreground">
          {renderInlineMarkdown(content.title, 'matching-title')}
        </h3>
      )}
      <ContentDescription text={content.description} markdown />

      {/* Instructions */}
      <div className="rounded-lg sm:rounded-xl border border-dashed border-slate-300 bg-slate-50/50 px-3 py-2.5 sm:p-4">
        <p className="text-xs sm:text-sm text-slate-600 text-center">
          <span className="font-medium text-slate-700">Tap to match!</span>{' '}
          <span className="hidden sm:inline">Select a prompt, then tap an option to connect them.</span>
          <span className="sm:hidden">Tap pairs to connect.</span>
          {!allMatched && matches.length > 0 && (
            <span className="block mt-1 text-[10px] sm:text-xs text-slate-500">
              {matches.length}/{pairs.length} matched
            </span>
          )}
          {allMatched && !isReadOnly && (
            <span className="block mt-1 text-[10px] sm:text-xs text-emerald-600 font-medium">
              ✓ Ready to submit!
            </span>
          )}
        </p>
      </div>

      {/* Matching Grid */}
      <div className="grid grid-cols-2 gap-2 sm:gap-4 max-w-2xl mx-auto">
        {/* Prompts Column */}
        <div className="space-y-2 sm:space-y-3">
          <h4 className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 sm:mb-3 text-center">
            Left Column
          </h4>
          {pairs.map((pair) => {
            const matchInfo = getMatchInfo(pair.promptId, 'prompt');
            const evaluation = evaluationMap.get(pair.promptId);

            return (
              <MatchCard
                key={pair.promptId}
                id={pair.promptId}
                label={pair.promptLabel}
                isSelected={selectedPromptId === pair.promptId}
                matchColor={
                  matchInfo.colorIndex !== null
                    ? MATCH_COLORS[matchInfo.colorIndex]
                    : null
                }
                matchNumber={matchInfo.matchNumber}
                evaluation={evaluation as any}
                isReadOnly={isReadOnly}
                onClick={() => handleCardClick(pair.promptId, 'prompt')}
                type="prompt"
                hasEvaluation={!!lastEvaluationDetails && !hasModifiedSinceEvaluation}
              />
            );
          })}
        </div>

        {/* Options Column */}
        <div className="space-y-2 sm:space-y-3">
          <h4 className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 sm:mb-3 text-center">
          Right Column
          </h4>
          {shuffledOptions.map((option) => {
            const matchInfo = getMatchInfo(option.optionId, 'option');
            const evaluation = evaluationMap.get(option.optionId);

            return (
              <MatchCard
                key={option.optionId}
                id={option.optionId}
                label={option.optionLabel}
                isSelected={selectedOptionId === option.optionId}
                matchColor={
                  matchInfo.colorIndex !== null
                    ? MATCH_COLORS[matchInfo.colorIndex]
                    : null
                }
                matchNumber={matchInfo.matchNumber}
                evaluation={evaluation as any}
                isReadOnly={isReadOnly}
                onClick={() => handleCardClick(option.optionId, 'option')}
                type="option"
                hasEvaluation={!!lastEvaluationDetails && !hasModifiedSinceEvaluation}
              />
            );
          })}
        </div>
      </div>

      {/* Match Summary - shows current matches (hidden on mobile for space) */}
      {matches.length > 0 && (!lastEvaluationDetails || hasModifiedSinceEvaluation) && (
        <div className="hidden sm:flex flex-wrap gap-2 justify-center pt-2">
          {matches.map((match, index) => {
            const prompt = pairs.find((p) => p.promptId === match.promptId);
            const option = shuffledOptions.find((o) => o.optionId === match.optionId);
            const color = MATCH_COLORS[match.colorIndex];

            if (!prompt || !option) return null;

            return (
              <div
                key={`match-${index}`}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-3 py-1.5',
                  'text-xs font-medium border',
                  color.bg,
                  color.border,
                  color.text
                )}
              >
                <span className="truncate max-w-[120px]">
                  {prompt.promptLabel}
                </span>
                <Link2 className="h-3 w-3 flex-shrink-0 opacity-60" />
                <span className="truncate max-w-[120px]">
                  {option.optionLabel}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <ExerciseFeedbackList feedback={feedback ?? []} pending={isSubmitting} />

      {onExerciseSubmit && !isReadOnly && (
        <div className="flex justify-center pt-2">
          <Button
            type="submit"
            disabled={!canSubmit || isSubmitting}
            variant="submit"
            size="submit"
            isLoading={isSubmitting}
            loadingText="Submitting..."
            className="w-full sm:w-auto min-w-[200px]"
          >
            {allMatched ? 'Submit matches' : `Submit ${matches.length} match${matches.length !== 1 ? 'es' : ''}`}
          </Button>
        </div>
      )}
    </form>
  );
}
